import type { ContentHash, ContentRef } from "@cogo/publisher-core";

export const ARTIFACT_REVISION_ENVELOPE_SCHEMA_VERSION =
  "cogo.generated-artifact-revision-envelope.v1" as const;

export const ARTIFACT_PERSISTENCE_RECEIPT_SCHEMA_VERSION =
  "cogo.generated-artifact-persistence-receipt.v1" as const;

export const GENERATED_ARTIFACT_APPROVAL_DECISION_SCHEMA_VERSION =
  "cogo.generated-artifact-approval-decision.v1" as const;

/**
 * Chat commits into the project that owns its IDE thread. Bot starts without
 * an IDE and therefore asks the persistence adapter to create a project owned
 * by the selected Bot profile. The resulting project id is reported by the
 * persistence receipt; it is never invented in the browser.
 */
export type GeneratedArtifactTarget =
  | {
      kind: "existing-project";
      projectId: string;
    }
  | {
      /**
       * A project created by a Bot remains Bot-owned after its first revision.
       * Keeping that owner in the target prevents a later Bot request from
       * being reinterpreted as an ordinary project Chat write.
       */
      kind: "existing-bot-project";
      projectId: string;
      ownerBotProfileId: string;
    }
  | {
      kind: "new-project";
      ownerBotProfileId: string;
      suggestedName: string;
    };

/**
 * An opaque version issued by the authority named in `authority`.
 *
 * Studio currently observes several legitimate version authorities (project
 * persistence, block `updated_at` CAS, and content-addressed evidence). This
 * contract does not coerce them into a number or reinterpret a content hash as
 * a project revision. Consumers compare a revision only within its authority.
 */
export interface ArtifactRevisionRef {
  authority: string;
  value: string;
}

/**
 * An approval is authority for exactly one generated revision. It is carried
 * into the persistence command so an adapter cannot reuse a decision for a
 * different artifact, request, run, revision, or content body.
 */
export interface GeneratedArtifactApprovalDecision {
  schemaVersion: typeof GENERATED_ARTIFACT_APPROVAL_DECISION_SCHEMA_VERSION;
  decisionId: string;
  artifactId: string;
  requestId: string;
  runId: string;
  actorId: string;
  actorKind: "human";
  decision: "approved" | "rejected";
  target: GeneratedArtifactTarget;
  revision: ArtifactRevisionRef;
  parentRevision: ArtifactRevisionRef | null;
  contentHash: ContentHash;
}

/**
 * Authenticated write/readback proof returned by the canonical persistence
 * adapter. Both refs are required: a successful write without an independent
 * readback is not a committed generated artifact.
 */
export interface ArtifactPersistenceReceipt {
  schemaVersion: typeof ARTIFACT_PERSISTENCE_RECEIPT_SCHEMA_VERSION;
  receiptId: string;
  projectId: string;
  ownerBotProfileId: string | null;
  artifactId: string;
  requestId: string;
  runId: string;
  approvalDecisionId: string;
  approvalActorId: string;
  approvalActorKind: "human";
  revision: ArtifactRevisionRef;
  contentHash: ContentHash;
  persistenceWriteRef: ContentRef;
  readbackRef: ContentRef;
  evidenceRefs: readonly ContentRef[];
  persistedAt: string;
}

/**
 * The cross-surface identity of one generated artifact revision.
 *
 * The same envelope travels from preview to approval to persistence. A Chat
 * caller supplies an `existing-project` target; a Bot caller supplies a
 * `new-project` target for revision one and an `existing-bot-project` target
 * for later revisions. Neither caller is allowed to replace the envelope after
 * approval with a local-store-only write.
 */
export interface ArtifactRevisionEnvelope {
  schemaVersion: typeof ARTIFACT_REVISION_ENVELOPE_SCHEMA_VERSION;
  artifactId: string;
  target: GeneratedArtifactTarget;
  revision: ArtifactRevisionRef;
  parentRevision: ArtifactRevisionRef | null;
  contentHash: ContentHash;
  requestId: string;
  runId: string;
  evidenceRefs: readonly ContentRef[];
  persistenceReceipt: ArtifactPersistenceReceipt | null;
}

export type UncommittedArtifactRevisionEnvelope = Omit<
  ArtifactRevisionEnvelope,
  "persistenceReceipt"
> & {
  persistenceReceipt: null;
};

export type CommittedArtifactRevisionEnvelope = Omit<
  ArtifactRevisionEnvelope,
  "revision" | "persistenceReceipt"
> & {
  revision: ArtifactRevisionRef;
  persistenceReceipt: ArtifactPersistenceReceipt;
};

export function artifactTargetProjectId(
  envelope: ArtifactRevisionEnvelope,
): string | null {
  return envelope.target.kind === "new-project"
    ? envelope.persistenceReceipt?.projectId ?? null
    : envelope.target.projectId;
}
